import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { colors } from "@/theme";
import EditIconSvg from "@/assets/edit_icon.svg";
import Pagination from '@mui/material/Pagination';
import CircularProgress from '@mui/material/CircularProgress';
import axios from 'axios';

interface CustomTableProps {
  columns: any[];
  rows: any[];
  getRowId: (row: any) => string | number;
  page?: number;
  pageSize?: number;
  rowCount?: number;
  onPageChange?: (event: React.ChangeEvent<unknown>, page: number) => void;
  loading?: boolean;
  sortModel?: { field: string; sort: 'asc' | 'desc' }[];
  onSortModelChange?: (model: { field: string; sort: 'asc' | 'desc' }[]) => void;
  onRefresh?: () => void;
}

const getCellValue = (col: any, row: any) => {
  // Priority: renderCell > valueGetter > valueFormatter > row[field]
  if (col.renderCell) {
    // DataGrid renderCell gets params: { row, value, ... }
    return col.renderCell({ row, value: row[col.field], field: col.field });
  }
  if (col.valueGetter) {
    // valueGetter gets (params: { row, value, field }) or (value, row)
    // Try both signatures
    try {
      if (col.valueGetter.length === 2) {
        return col.valueGetter(row[col.field], row);
      }
      return col.valueGetter({ row, value: row[col.field], field: col.field });
    } catch {
      return row[col.field];
    }
  }
  if (col.valueFormatter) {
    // valueFormatter gets (params: { value, row, field }) or (value)
    try {
      if (col.valueFormatter.length === 1) {
        return col.valueFormatter(row[col.field]);
      }
      return col.valueFormatter({ value: row[col.field], row, field: col.field });
    } catch {
      return row[col.field];
    }
  }
  return row[col.field];
};

const CustomTable: React.FC<CustomTableProps> = ({ columns, rows, getRowId, page = 1, pageSize = 20, rowCount = 0, onPageChange, loading = false, sortModel = [], onSortModelChange, onRefresh }): React.ReactElement => {
  const [openRows, setOpenRows] = useState<(string | number)[]>([]);
  const [editingRowId, setEditingRowId] = useState<string | number | null>(null);
  const [editCounts, setEditCounts] = useState<{ [key: string]: number }>({});
  const [hoveredCol, setHoveredCol] = useState<string | null>(null);
  const [savingStates, setSavingStates] = useState<{ [key: string]: boolean }>({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingSave, setPendingSave] = useState<null | {
    productId: string | number;
    inventoryCountId: string | number;
    counted: number;
    createdBy: string;
  }>(null);

  const handleExpandClick = (rowId: string | number) => {
    setOpenRows(prev => {
      if (prev.includes(rowId)) {
        return prev.filter(id => id !== rowId);
      }
      return [...prev, rowId];
    });
    setEditingRowId(null);
    setEditCounts({});
  };

  const handleEditClick = (row: any) => {
    setEditingRowId(getRowId(row));
    setOpenRows(prev => {
      const rowId = getRowId(row);
      if (!prev.includes(rowId)) {
        return [...prev, rowId];
      }
      return prev;
    });
    // Prepare initial edit values for all counts in expanded row
    if (Array.isArray(row.inventoryCountWithInventoryCountProducts)) {
      const initialCounts: { [key: string]: number } = {};
      row.inventoryCountWithInventoryCountProducts.forEach((prod: any) => {
        initialCounts[prod.inventoryCountProductId] = prod.counted;
      });
      setEditCounts(initialCounts);
    }
  };

  const handleEditCountChange = (id: string | number, value: string) => {
    setEditCounts((prev) => ({ ...prev, [id]: Number(value) }));
  };

  const handleCancelEdit = () => {
    setEditingRowId(null);
    setEditCounts({});
  };

  const handleDialogUpdate = async () => {
    if (!pendingSave) return;
    const { productId, inventoryCountId, counted, createdBy } = pendingSave;
    try {
      setSavingStates(prev => ({ ...prev, [productId]: true }));
      await axios.post(`${process.env.REACT_APP_REST_BASE_URL}/InventoryCounts/updateproductcount`, {
        invertoryCountId: inventoryCountId,
        productId: productId,
        counted: counted,
        createdBy: createdBy
      });
      setEditingRowId(null);
      setEditCounts({});
      setSavingStates(prev => ({ ...prev, [productId]: false }));
      setDialogOpen(false);
      setPendingSave(null);
      if (typeof onRefresh === 'function') onRefresh();
    } catch (error) {
      console.error('Error saving count:', error);
      setSavingStates(prev => ({ ...prev, [productId]: false }));
      setDialogOpen(false);
      setPendingSave(null);
    }
  };

  const handleSaveClick = (productId: string | number, inventoryCountId: string | number, counted: number, createdBy: string) => {
    setPendingSave({ productId, inventoryCountId, counted, createdBy });
    setDialogOpen(true);
  };

  return (
    <Box position="relative">
      {/* Confirmation Dialog for Save */}
      <Dialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setPendingSave(null); }}
        PaperProps={{
          sx: {
            borderRadius: '18px',
            minWidth: 380,
            boxShadow: '0 2px 16px rgba(0,0,0,0.10)',
            p: 2,
          }
        }}
      >
        <DialogContent sx={{ textAlign: 'center', pt: 5, pb: 2 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 500, color: '#2d3748', mb: 1 }}>
            Are you sure you want to adjust<br />this count by {pendingSave?.counted ?? ''}?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 4, pt: 0 }}>
          <Button
            onClick={() => { setDialogOpen(false); setPendingSave(null); }}
            variant="outlined"
            sx={{
              minWidth: 140,
              fontFamily:'PF DIN Text Pro',
              borderRadius: 2,
              border: `1px solid ${colors.neutralGrey700}`,
              color: colors.neutralGrey800,
              fontWeight: 500,
              fontSize: 17,
              background: '#fff',
              mr: 2,
              boxShadow: 'none',
              '&:hover': { background: '#ededed', borderColor: colors.neutralGrey800 }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDialogUpdate}
            variant="contained"
            sx={{
              minWidth: 140,
              fontFamily:'PF DIN Text Pro',
              borderRadius: 2,
              background: colors.primaryOzone500,
              color: '#fff',
              fontWeight: 500,
              fontSize: 17,
              boxShadow: 'none',
              '&:hover': { background: '#0056d6' }
            }}
            disabled={!!(pendingSave && savingStates[pendingSave.productId])}
          >
            {pendingSave && savingStates[pendingSave.productId] ? <CircularProgress size={28} sx={{color:colors.white}} /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
      <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)', borderRadius: 2 }}>
        <Table sx={{ minWidth: 900, background: '#fff', borderCollapse: 'separate', borderSpacing: 0 }}>
          <TableHead>
            <TableRow sx={{ height: 56, background: '#fafbfc' }}>
              {
                columns.map((col) => {
                  const isSorted = sortModel?.[0]?.field === col.field;
                  const sortDirection = isSorted ? sortModel[0].sort : undefined;
                  const sortable = col.sortable !== false;
                  const showSortIcon = (hoveredCol === col.field) || isSorted;
                  return (
                    <TableCell
                      key={col.field}
                      align={col.align || 'left'}
                      sx={{
                        fontWeight: 700,
                        fontSize: '15px',
                        textTransform: 'uppercase',
                        color: '#1a202c',
                        borderTop: `1px solid ${colors.neutralGrey800}`,
                        background: '#fafbfc',
                        letterSpacing: '0.5px',
                        minHeight: 56,
                        maxHeight: 56,
                        borderBottom: `1px solid ${colors.neutralGrey800}`,
                        cursor: sortable ? 'pointer' : 'default',
                        userSelect: 'none',
                        position: 'relative',
                      }}
                      onMouseEnter={() => setHoveredCol(col.field)}
                      onMouseLeave={() => setHoveredCol(null)}
                      onClick={() => {
                        if (!sortable) return;
                        if (!onSortModelChange) return;
                        if (isSorted) {
                          // Cycle: desc -> asc -> none -> desc ...
                          if (sortDirection === 'desc') {
                            onSortModelChange([{ field: col.field, sort: 'asc' }]);
                          } else if (sortDirection === 'asc') {
                            onSortModelChange([]); // clear sort
                          }
                        } else {
                          // Not sorted: start with desc
                          onSortModelChange([{ field: col.field, sort: 'desc' }]);
                        }
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={0.5}>
                        {col.headerName}
                        {sortable && (
                          isSorted ? (
                            sortDirection === 'asc' ? (
                              <ArrowUpwardIcon fontSize="small" sx={{ opacity: 1 }} />
                            ) : (
                              <ArrowDownwardIcon fontSize="small" sx={{ opacity: 1 }} />
                            )
                          ) : (
                            <ArrowDownwardIcon fontSize="small" sx={{ opacity: hoveredCol === col.field ? 0.5 : 0.2 }} />
                          )
                        )}
                      </Box>
                    </TableCell>
                  );
                })
              }
              <TableCell sx={{ width: 48, background: '#fafbfc', border: 'none', borderTop: `1px solid ${colors.neutralGrey800}`, borderBottom: `1px solid ${colors.neutralGrey800}` }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, idx) => {
              const rowId = getRowId(row);
              return (
                <React.Fragment key={rowId}>
                  <TableRow
                    hover
                    onClick={() => handleExpandClick(rowId)}
                    sx={{
                      minHeight: 56,
                      background: editingRowId === rowId ? '#E5F1FF' : (idx % 2 === 0 ? '#fff' : '#f9f9f9'),
                      borderBottom: openRows.includes(rowId) ? 'none' : `1px solid ${colors.neutralGrey800}`,
                      transition: 'background 0.2s',
                      cursor: 'pointer',
                    }}
                  >
                    {columns.map((col) => {
                      let cellValue = getCellValue(col, row);
                      let cellSx = {
                        fontSize: '16px',
                        color: '#2d3748',
                        padding: '12px 8px',
                        borderBottom: openRows.includes(rowId) ? 'none' : `1px solid ${colors.neutralGrey800}`,
                        fontWeight: col.type === 'number' ? 600 : 400,
                        background: 'inherit',
                        verticalAlign: 'middle',
                      };
                      // Special color for discrepancy column
                      if (col.field === 'discrepancy' && typeof cellValue === 'number') {
                        if (cellValue < 0) {
                          cellSx.color = '#E53935'; // Red
                        } else if (cellValue > 0) {
                          cellSx.color = '#2ECC40'; // Green
                        }
                      }
                      // Render Edit icon for actions column
                      if (col.field === 'actions') {
                        const hasExpandable = Array.isArray(row.inventoryCountWithInventoryCountProducts) && row.inventoryCountWithInventoryCountProducts.length > 0;
                        // Only show Cancel if editing and row is expandable
                        if (editingRowId === getRowId(row) && hasExpandable) {
                          return (
                            <TableCell
                              key={col.field}
                              align={col.align || 'left'}
                              sx={cellSx}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Button
                                variant="outlined"
                                size="small"
                                sx={{ minWidth: 80, borderRadius: 2, borderColor: colors.neutralGrey800, color: colors.neutralGrey800, background: '#fff', fontWeight: 500 }}
                                onClick={handleCancelEdit}
                              >
                                Cancel
                              </Button>
                            </TableCell>
                          );
                        }
                        return (
                          <TableCell
                            key={col.field}
                            align={col.align || 'left'}
                            sx={cellSx}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <IconButton 
                              size="small" 
                              color="primary" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditClick(row);
                              }}
                            >
                              <img src={EditIconSvg} alt="Edit" style={{ width: 24, height: 24 }} />
                            </IconButton>
                          </TableCell>
                        );
                      }
                      return (
                        <TableCell
                          key={col.field}
                          align={col.align || 'left'}
                          sx={{
                            ...cellSx,
                            ...(col.width && { width: col.width, maxWidth: col.maxWidth || col.width }),
                            ...(col.maxWidth && { maxWidth: col.maxWidth }),
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {cellValue}
                        </TableCell>
                      );
                    })}
                    <TableCell 
                      sx={{ 
                        width: 48, 
                        border: 'none', 
                        background: 'inherit', 
                        borderBottom: openRows.includes(rowId) ? 'none' : `1px solid ${colors.neutralGrey800}`, 
                        borderTop: 'none' 
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => handleExpandClick(rowId)}
                      >
                        {openRows.includes(rowId) ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  {/* Expandable content placeholder */}
                  <TableRow>
                    <TableCell style={{ padding:0 }} colSpan={columns.length + 1}>
                      <Collapse in={openRows.includes(rowId)} timeout="auto" unmountOnExit sx={{ background: editingRowId === rowId ? '#E5F1FF' : 'transparent' }}>
                        <Box margin={1} sx={{ background: editingRowId === rowId ? '#E5F1FF' : 'transparent' }}>
                          {Array.isArray(row.inventoryCountWithInventoryCountProducts) && row.inventoryCountWithInventoryCountProducts.length > 0 ? (
                            <Table size="small" sx={{ minWidth: 400, borderRadius: 1 }}>
                              <TableBody>
                                {row.inventoryCountWithInventoryCountProducts.map((prod: any, idx: number) => {
                                  // If editing, show input fields for counted
                                  const isEditing = editingRowId === rowId;
                                  return (
                                    <TableRow key={prod.inventoryCountProductId || idx}>
                                      <TableCell sx={{paddingLeft: '100px', fontSize: '15px', color: '#2d3748', border: 'none', fontWeight: 600, width: isEditing ? '300px' : '335px' }}>{prod.inventoryCountProductCreatedBy}</TableCell>
                                      <TableCell sx={{ fontSize: '15px', color: '#2d3748', border: 'none', fontWeight: 600 }}>
                                        {isEditing ? (
                                          <Box display="flex" alignItems="center" gap={1}>
                                            <TextField
                                              size="small"
                                              type="number"
                                              value={editCounts[prod.inventoryCountProductId] ?? prod.counted}
                                              onChange={e => handleEditCountChange(prod.inventoryCountProductId, e.target.value)}
                                              sx={{ width: 80, background: '#fff' }}
                                              disabled={savingStates[prod.inventoryCountProductId]}
                                            />
                                            <Button
                                              variant="contained"
                                              size="small"
                                              sx={{ 
                                                minWidth: 60, 
                                                padding: '6px 12px', 
                                                borderRadius: 2, 
                                                background: colors.primaryOzone500, 
                                                color: '#fff', 
                                                fontWeight: 500, 
                                                boxShadow: 'none' 
                                              }}
                                              onClick={() => handleSaveClick(
                                                rowId,
                                                prod.inventoryCountId,
                                                editCounts[prod.inventoryCountProductId] ?? prod.counted,
                                                prod.inventoryCountProductCreatedBy
                                              )}
                                              disabled={savingStates[prod.inventoryCountProductId]}
                                            >
                                              Save
                                            </Button>
                                          </Box>
                                        ) : (
                                          prod.counted
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              No details available.
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
        {/* Pagination Controls */}
        {rowCount > pageSize && (
          <Box display="flex" justifyContent="center" alignItems="center" mt={2} mb={2}>
            <Pagination
              count={Math.ceil(rowCount / pageSize)}
              page={page}
              onChange={onPageChange}
              color="primary"
            />
          </Box>
        )}
      </TableContainer>
      {loading && (
        <Box position="absolute" top={0} left={0} width="100%" height="100%" display="flex" alignItems="center" justifyContent="center" bgcolor="rgba(255,255,255,0.6)" zIndex={10}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default CustomTable; 